import produce from "immer";
import { ReducerBuilder } from "./ReducerBuilder";
import * as billActions from "../actions/bill";
import {
  calculateBillItems,
  calculateCreditNoteBillItems,
  calculateCreditNoteRefundAndPaymentInfo,
  calculateCreditNoteSummary,
  calculateSummary,
  distributePayment,
  getTemplateBillItem,
  mergeBatchInfo,
  updateShowFields
} from "../actions/helpers/billHelper";
import {
  BillDocumentType,
  BillItemType,
  BillType,
  DocumentTypes
} from "../interfaces/BillInterfaces";
import { removeBill } from "./billsFunction";
import { BillEditorWarning } from "../containers/Billing/Editor/BillEditor";

export interface StateBillsInterface {
  collection: BillType[];
  draft: BillDocumentType | null;
  isScanning: boolean;
  warning: BillEditorWarning;
}

const INITIAL_STATE: StateBillsInterface = {
  collection: [],
  draft: null,
  isScanning: false,
  warning: { message: "", type: null }
};

function getBill(state, payload) {
  return produce(state, (draftItem) => {
    const payloadWithBatchInfo = produce(payload, (draftSubItem) => {
      draftSubItem.document.billItems = mergeBatchInfo(draftSubItem.document.billItems);
    });
    const idx = draftItem.collection.findIndex((el) => el.id === payloadWithBatchInfo.id);
    if (idx !== -1) {
      draftItem.collection[idx] = payloadWithBatchInfo;
    } else {
      draftItem.collection.push(payloadWithBatchInfo);
    }
  });
}

function updateBillsInBatch(state, payload) {
  if (!payload) return state;
  return produce(state, (draftItem) => {
    const ids = payload.map(({ id }) => id);
    draftItem.collection = draftItem.collection.map((el) =>
      ids.includes(el.id) ? payload.find(({ id }) => id === el.id) : el
    );
  });
}

// ordering the bill so that the credited bill is after its related bill
export const getOrderedBill = (billList: BillDocumentType[]): BillDocumentType[] => {
  const sortedBills = [...billList].sort((a, b) => b.billNumber - a.billNumber);
  return sortedBills;
};

function addBills(state, payload) {
  // use this for pagination
  return { ...state, collection: getOrderedBill(payload.results), total: payload.total };
  // return { ...state, collection: payload };
}

function initDraft(state, payload) {
  return { ...state, draft: payload };
}

function clearDraft(state) {
  return { ...state, draft: null };
}

const updatesNo = (draft) => ({
  ...draft,
  billItems: draft.billItems.map((billItem, i) => ({ ...billItem, sNo: i + 1 }))
});

function addNewRow(state) {
  let newDraft = {
    ...state.draft,
    billItems: [...state.draft.billItems, getTemplateBillItem()]
  };
  newDraft = updatesNo(newDraft);
  return {
    ...state,
    draft: newDraft
  };
}

function calculateDraftValuesForCreditNote(draft, originalBill) {
  let draftUpdated = calculateCreditNoteBillItems(draft, originalBill);
  draftUpdated = calculateCreditNoteSummary(draftUpdated);
  draftUpdated = calculateCreditNoteRefundAndPaymentInfo({
    draft: draftUpdated,
    originalBill
  });

  return draftUpdated;
}

function calculateDraftValues(draft) {
  let draftUpdated = updateShowFields(draft);
  draftUpdated = calculateBillItems(draftUpdated);
  draftUpdated = calculateSummary(draftUpdated);
  draftUpdated = distributePayment(draftUpdated);

  return draftUpdated;
}

function removeRow(state, sNo: number) {
  if (!state.draft) return state;

  let newDraft: BillDocumentType;

  // NOTE: in both cases, we can just remove the item from the billItems to get same result
  // But I am just following the current implementation to avoid any regression

  if (state.draft.type === DocumentTypes.CREDITNOTE) {
    newDraft = {
      ...state.draft,
      billItems: state.draft.billItems.map((item: BillItemType) =>
        item.sNo === sNo ? { ...item, returned: !item.returned } : item
      )
    };
    const sourceBill = state.collection.find(({ id }) => id === newDraft.referenceTo);
    newDraft = calculateDraftValuesForCreditNote(newDraft, sourceBill);
  } else {
    newDraft = {
      ...state.draft,
      billItems: state.draft.billItems
        .filter((item: BillItemType) => item.sNo !== sNo)
        .sort((a: BillItemType, b: BillItemType) => (a.sNo > b.sNo ? 1 : -1))
    };
    newDraft = updatesNo(newDraft);
    newDraft = calculateDraftValues(newDraft);
  }

  return {
    ...state,
    draft: newDraft
  };
}

function updateDraft(state, payload) {
  let calculatedDraft;
  const clonedPayload = structuredClone(payload);

  if (payload.type === DocumentTypes.CREDITNOTE) {
    const sourceBill = state.collection.find(({ id }) => id === payload.referenceTo);
    calculatedDraft = calculateDraftValuesForCreditNote(payload, sourceBill);
  } else {
    calculatedDraft = calculateDraftValues(clonedPayload);
  }
  return { ...state, draft: calculatedDraft };
}

function updateDraftSettings(state, payload) {
  const newDraft = produce(state.draft, (draft) => {
    draft.settings = payload;
  });

  const calculatedDraft = calculateDraftValues(newDraft);
  return { ...state, draft: calculatedDraft };
}

function updateBillOnClientEdit(state, payload) {
  if (!payload?.id) return state;
  const newCollection = state.collection.map((bill) => {
    if (payload.id === bill.client.id) {
      return produce(bill, (draft) => {
        draft.client = payload;
      });
    }
    return bill;
  });
  return { ...state, collection: newCollection };
}

function updateBillPrintCount(state, payload) {
  if (!payload?.id) return state;
  return produce(state, (draftItem) => {
    const idx = draftItem.collection.findIndex((el) => el.id === payload.id);
    if (idx !== -1) {
      draftItem.collection[idx].printCount = payload.printCount;
    }
  });
}

const updateOnReceiptCancel = (state, payload) => {
  if (!payload?.id) return state;
  return produce(state, (draft) => {
    const idx = draft.collection.findIndex((el) => el.id === payload.id);
    if (idx !== -1) {
      draft.collection[idx].paymentInfo = payload.paymentInfo;
      draft.collection[idx].receipts = payload.receipts;
    }
  });
};

const billCancel = (state, payload) => {
  if (!payload?.id) return state;
  return produce(state, (draft) => {
    const idx = draft.collection.findIndex((el) => el.id === payload.id);
    if (idx !== -1) {
      draft.collection[idx].paymentInfo = payload.paymentInfo;
      draft.collection[idx].receipts = payload.receipts;
      draft.collection[idx].status = payload.status;
    }
  });
};

const scannerDetected = (state, payload) => ({ ...state, isScanning: payload });
const setWarning = (state, payload) => ({ ...state, warning: payload });

const reducer = ReducerBuilder.create(INITIAL_STATE)
  .mapAction(billActions.Type.INIT_DRAFT, initDraft)
  .mapAction(billActions.Type.CLEAR_DRAFT, clearDraft)
  .mapAction(billActions.Type.ADD_NEW_ROW, addNewRow)
  .mapAction(billActions.Type.REMOVE_ROW, removeRow)
  .mapAction(billActions.Type.UPDATE_DRAFT, updateDraft)
  .mapAction(billActions.Type.UPDATE_DRAFT_SETTINGS, updateDraftSettings)
  .mapAction(billActions.Type.FETCH_BILLS, addBills)
  .mapAction(billActions.Type.GET_BILL, getBill)
  .mapAction(billActions.Type.UPDATE_BILL, getBill)
  .mapAction(billActions.Type.UPDATE_BILLS, updateBillsInBatch)
  .mapAction(billActions.Type.REMOVE_BILL, removeBill)
  .mapAction(billActions.Type.UPDATE_BILL_ON_CLIENT_EDIT, updateBillOnClientEdit)
  .mapAction(billActions.Type.UPDATE_PRINT_COUNT, updateBillPrintCount)
  .mapAction(billActions.Type.RECEIPT_CANCEL, updateOnReceiptCancel)
  .mapAction(billActions.Type.BILL_CANCELLATION, billCancel)
  .mapAction(billActions.Type.BARCODE_SCANNER_DETECTED, scannerDetected)
  .mapAction(billActions.Type.SET_WARNING, setWarning)
  .build();
export default reducer;
